

// ===== Sort_by 商品置顶（简化版）=====
// 功能：通过 URL 参数 Sort_by 指定商品 handle 并置顶。
// 说明：不使用占位元素，获取后直接插入列表。
// 用法：
// - 单个：?Sort_by=handle
// - 多个：?Sort_by=handle1,handle2
class CollectionSortByPin {
    // 初始化实例并注入 API。
    constructor(api) {
        this.api = api;
    }

    // 提取 ID 字符串中的数字部分。
    static extractNumericId(value) {
        if (!value) return null;
        const match = String(value).match(/(\d+)(?:\?.*)?$/);
        return match ? match[1] : null;
    }

    // 将价格转换为分单位的数字。
    static toCents(value) {
        if (value == null) return null;
        const rawValue = typeof value === 'object' && value.amount != null ? value.amount : value;
        const numberValue = Number(rawValue);
        if (Number.isNaN(numberValue)) return null;
        return Math.round(numberValue * 100);
    }

    // 生成指定尺寸的图片 URL。
    static buildSizedImageUrl(baseImageUrl, size) {
        if (!baseImageUrl) return baseImageUrl;
        const [path, query] = baseImageUrl.split('?');
        if (!/\.(jpg|jpeg|png|gif|webp)$/i.test(path)) return baseImageUrl;
        const resized = path.replace(/\.(jpg|jpeg|png|gif|webp)$/i, `_${size}.$1`);
        return query ? `${resized}?${query}` : resized;
    }

    // 规范化商品数据为统一结构。
    static normalizeProductData(rawProduct) {
        if (!rawProduct) return null;
        const images = Array.isArray(rawProduct.images)
            ? rawProduct.images
            : rawProduct.images?.edges?.map((edge) => edge.node) || [];
        const variants = Array.isArray(rawProduct.variants)
            ? rawProduct.variants
            : rawProduct.variants?.edges?.map((edge) => edge.node) || [];
        const normalizedImages = images
            .map((image) => ({
                url: image?.url || image?.src || null,
                alt: image?.altText || image?.alt || null,
            }))
            .filter((image) => image.url);
        const featuredImage = rawProduct.featured_image || rawProduct.image || normalizedImages[0]?.url || null;

        return {
            id: rawProduct.id,
            numericId: CollectionSortByPin.extractNumericId(rawProduct.id) || rawProduct.idNumeric || null,
            title: rawProduct.title,
            handle: rawProduct.handle,
            featured_image: featuredImage,
            images: normalizedImages,
            variants: variants.map((variant) => ({
                id: variant.id,
                numericId: CollectionSortByPin.extractNumericId(variant.id),
                price: CollectionSortByPin.toCents(variant.price?.amount ?? variant.price),
                compare_at_price: CollectionSortByPin.toCents(
                    variant.compareAtPrice?.amount ?? variant.compare_at_price
                ),
            })),
        };
    }

    // 将生成的商品元素插入到列表顶部。
    insertProductElement(productElement) {
        try {
            const productListInner = document.querySelector('.product-list__inner');
            if (!productListInner) {
                console.warn('无法找到产品列表容器');
                return false;
            }

            const customFirstItem = productListInner.querySelector('.product-list__item--custom');
            if (customFirstItem) {
                productListInner.insertBefore(productElement, customFirstItem.nextSibling);
            } else {
                productListInner.insertBefore(productElement, productListInner.firstChild);
            }

            this.reinitializeProductItem(productElement);
            console.log('成功插入产品元素到列表');
            return true;
        } catch (error) {
            console.error('插入产品元素失败:', error);
            return false;
        }
    }

    // 重新初始化插入后的商品节点状态。
    reinitializeProductItem(productElement) {
        if (!productElement) return;

        if (window.customElements?.upgrade) {
            window.customElements.upgrade(productElement);
        }

        const productList = productElement.closest('product-list');
        if (productList && Array.isArray(productList.productItems)) {
            productList.productItems = Array.from(productList.querySelectorAll('product-item'));
            if (productList._animation) {
                productList._animation = null;
            }
        }
    }

    // 基于模板生成商品卡片 HTML。
    async generateProductItemHtml(productData) {
        try {
            const existingProductItem = document.querySelector('product-item:not(.product-item--api-loaded)');
            if (!existingProductItem) {
                console.error('无法找到product-item模板');
                return null;
            }

            const newProductItem = existingProductItem.cloneNode(true);

            const productLinks = newProductItem.querySelectorAll('a[href*="/products/"]');
            productLinks.forEach((link) => {
                link.href = `/products/${productData.handle}`;
            });

            const primaryImages = newProductItem.querySelectorAll('img.product-item__primary-image');
            const primaryImageUrl = productData.featured_image || productData.images?.[0]?.url || null;
            if (primaryImages.length > 0 && primaryImageUrl) {
                primaryImages.forEach((img) => {
                    let imageUrl = primaryImageUrl;
                    if (img.getAttribute('width') && img.getAttribute('height')) {
                        const width = img.getAttribute('width');
                        const height = img.getAttribute('height');
                        imageUrl = CollectionSortByPin.buildSizedImageUrl(primaryImageUrl, `${width}x${height}`);
                    } else {
                        imageUrl = CollectionSortByPin.buildSizedImageUrl(primaryImageUrl, '400x400');
                    }

                    img.src = imageUrl;
                    img.alt = productData.title;

                    if (img.hasAttribute('srcset')) {
                        const srcsetSizes = ['200', '300', '400', '500', '600'];
                        const srcsetValues = srcsetSizes.map((size) => {
                            const sizedUrl = CollectionSortByPin.buildSizedImageUrl(primaryImageUrl, `${size}x${size}`);
                            return `${sizedUrl} ${size}w`;
                        });
                        img.srcset = srcsetValues.join(', ');
                    }
                });
            }

            const imageWrapper = newProductItem.querySelector('.product-item__image-wrapper');
            const secondaryImageUrl = productData.images?.[1]?.url || null;
            let secondaryImage = newProductItem.querySelector('img.product-item__secondary-image');

            if (secondaryImageUrl) {
                if (!secondaryImage) {
                    const fallbackPrimary = primaryImages[0] || newProductItem.querySelector('img');
                    if (fallbackPrimary?.parentNode) {
                        secondaryImage = fallbackPrimary.cloneNode(true);
                        secondaryImage.classList.remove('product-item__primary-image');
                        secondaryImage.classList.add('product-item__secondary-image');
                        secondaryImage.removeAttribute('data-media-id');
                        fallbackPrimary.parentNode.insertBefore(secondaryImage, fallbackPrimary.nextSibling);
                    }
                }

                if (secondaryImage) {
                    let imageUrl = secondaryImageUrl;
                    if (secondaryImage.getAttribute('width') && secondaryImage.getAttribute('height')) {
                        const width = secondaryImage.getAttribute('width');
                        const height = secondaryImage.getAttribute('height');
                        imageUrl = CollectionSortByPin.buildSizedImageUrl(secondaryImageUrl, `${width}x${height}`);
                    } else {
                        imageUrl = CollectionSortByPin.buildSizedImageUrl(secondaryImageUrl, '400x400');
                    }

                    secondaryImage.src = imageUrl;
                    secondaryImage.alt = productData.title;

                    if (secondaryImage.hasAttribute('srcset')) {
                        const srcsetSizes = ['200', '300', '400', '500', '600'];
                        const srcsetValues = srcsetSizes.map((size) => {
                            const sizedUrl = CollectionSortByPin.buildSizedImageUrl(
                                secondaryImageUrl,
                                `${size}x${size}`
                            );
                            return `${sizedUrl} ${size}w`;
                        });
                        secondaryImage.srcset = srcsetValues.join(', ');
                    }
                }

                if (imageWrapper && !imageWrapper.classList.contains('product-item__image-wrapper--multiple')) {
                    imageWrapper.classList.add('product-item__image-wrapper--multiple');
                }
            } else {
                if (secondaryImage) {
                    secondaryImage.remove();
                }
                if (imageWrapper) {
                    imageWrapper.classList.remove('product-item__image-wrapper--multiple');
                }
            }

            const productTitles = newProductItem.querySelectorAll(
                '.product-item-meta__title, .product-item__title, h3, .heading'
            );
            productTitles.forEach((title) => {
                if (title.tagName.toLowerCase() === 'a' || title.closest('a')) {
                    const textNode = title.tagName.toLowerCase() === 'a' ? title : title.closest('a');
                    if (textNode) textNode.textContent = productData.title;
                } else if (title.textContent.trim() !== '') {
                    title.textContent = productData.title;
                }
            });

            const productPrices = newProductItem.querySelectorAll('.price, .product-item__price, .money');
            if (productPrices.length > 0 && productData.variants && productData.variants.length > 0) {
                const variant = productData.variants[0];
                const price = typeof variant.price === 'number' ? variant.price / 100 : null;
                const comparePrice =
                    typeof variant.compare_at_price === 'number' ? variant.compare_at_price / 100 : null;

                productPrices.forEach((priceElement) => {
                    if (
                        priceElement.classList.contains('price--compare') ||
                        priceElement.classList.contains('compare-at-price') ||
                        priceElement.closest('.price--compare')
                    ) {
                        if (comparePrice) {
                            priceElement.textContent = `$${comparePrice.toFixed(2)}`;
                        } else {
                            priceElement.style.display = 'none';
                        }
                    } else {
                        if (price != null) {
                            priceElement.textContent = `$${price.toFixed(2)}`;
                        }
                    }
                });
            }

            const productIdValue = productData.numericId || productData.id;
            const elementsWithProductId = newProductItem.querySelectorAll('[data-product-id]');
            elementsWithProductId.forEach((element) => {
                if (productIdValue) {
                    element.setAttribute('data-product-id', productIdValue);
                }
            });

            if (productData.variants && productData.variants.length > 0) {
                const firstVariant = productData.variants[0];
                const variantIdValue = firstVariant.numericId || firstVariant.id;
                const elementsWithVariantId = newProductItem.querySelectorAll('[data-variant-id]');
                elementsWithVariantId.forEach((element) => {
                    if (variantIdValue) {
                        element.setAttribute('data-variant-id', variantIdValue);
                    }
                });
            }

            const forms = newProductItem.querySelectorAll('form');
            forms.forEach((form) => {
                const variantInput = form.querySelector('input[name="id"]');
                if (variantInput && productData.variants && productData.variants.length > 0) {
                    const variantIdValue = productData.variants[0].numericId || productData.variants[0].id;
                    if (variantIdValue) {
                        variantInput.value = variantIdValue;
                    }
                }
            });

            const elementsWithId = newProductItem.querySelectorAll('[id]');
            elementsWithId.forEach((element) => {
                const currentId = element.id;
                if (currentId.includes('product') || currentId.includes('ProductItem')) {
                    const newId = `${currentId}-api-${productIdValue || 'unknown'}`;
                    element.id = newId;
                }
            });

            const elementsWithHandle = newProductItem.querySelectorAll('[data-product-handle]');
            elementsWithHandle.forEach((element) => {
                element.setAttribute('data-product-handle', productData.handle);
            });

            newProductItem.classList.add('product-item--api-loaded');
            newProductItem.style.opacity = 1;

            console.log(`成功生成商品HTML: ${productData.title}`);
            return newProductItem;
        } catch (error) {
            console.error('生成商品HTML失败:', error, productData);
            return null;
        }
    }

    // 标准化数据并插入商品元素。
    async insertProductByData(productData) {
        const normalized = CollectionSortByPin.normalizeProductData(productData);
        if (!normalized) return null;
        const productElement = await this.generateProductItemHtml(normalized);
        if (productElement) {
            this.insertProductElement(productElement);
            return productElement;
        }
        return null;
    }

    // 通过服务端渲染片段获取商品卡片 DOM。
    async fetchProductItemElement(handle) {
        if (!handle) return null;
        try {
            const collectionHandle = CollectionSortByPin.getCollectionHandleFromUrl();
            const productPath = collectionHandle
                ? `/collections/${collectionHandle}/products/${handle}`
                : `/products/${handle}`;
            const response = await fetch(`${productPath}?section_id=product-card`, {
                credentials: 'same-origin',
            });
            if (!response.ok) {
                console.warn(`获取商品卡片片段失败: ${handle}`, response.status);
                return null;
            }

            const html = await response.text();
            const temp = document.createElement('div');
            temp.innerHTML = html;
            const productElement = temp.querySelector('product-item');
            if (!productElement) {
                console.warn(`片段中未找到 product-item: ${handle}`);
                return null;
            }

            productElement.classList.add('product-item--api-loaded');
            productElement.style.opacity = 1;
            return productElement;
        } catch (error) {
            console.error(`获取商品卡片片段失败: ${handle}`, error);
            return null;
        }
    }

    // 通过商品 handle 获取并插入商品。
    async getProductByHandle(handle) {
        try {
            const productElement = await this.fetchProductItemElement(handle);
            console.log(`成功获取商品卡片(handle): ${handle}`);
            if (!productElement) {
                console.warn(`无法生成商品HTML元素: ${handle}`);
                return null;
            }
            this.insertProductElement(productElement);
            return productElement;
        } catch (error) {
            console.error(`获取商品 ${handle} 失败:`, error);
            return null;
        }
    }

    // 通过数字商品 ID 获取并插入商品。
    async getProductById(numericId) {
        try {
            const gid = `gid://shopify/Product/${numericId}`;
            const query = `
                query ($id: ID!) {
                product(id: $id) {
                    id
                    title
                    handle
                }
                }
            `;
            const result = await this.api.shopifyFetch(query, { id: gid }, { useCache: true });
            const productData = result?.product;
            if (!productData) {
                console.warn(`无法获取商品ID ${numericId}`);
                return null;
            }
            console.log(`成功获取商品数据(id): ${numericId}`, productData.title);
            const productElement = await this.fetchProductItemElement(productData.handle);
            if (!productElement) {
                console.warn(`无法生成商品HTML元素: ${numericId}`);
                return null;
            }
            this.insertProductElement(productElement);
            return productElement;
        } catch (error) {
            console.error(`获取商品ID ${numericId} 失败:`, error);
            return null;
        }
    }

    // 判断字符串是否为纯数字 ID。
    static isNumericId(value) {
        return /^\d+$/.test(value);
    }

    // 从当前 URL 中提取集合 handle（用于保留 collection 上下文）。
    static getCollectionHandleFromUrl() {
        const match = window.location.pathname.match(/\/collections\/([^/]+)/);
        return match ? match[1] : null;
    }

    // 解析 URL 参数并执行置顶流程。
    async start() {
        if (!this.api) {
            console.warn('ShopifyStorefrontAPI 未就绪，无法执行商品置顶逻辑');
            return;
        }
        const Sort_by = new URLSearchParams(window.location.search).get('Sort_by');
        if (!Sort_by) return;
        const Sort_by_arr = Sort_by.split(',')
            .map((item) => item.trim())
            .filter(Boolean)
            .reverse();
        for (const item of Sort_by_arr) {
            if (CollectionSortByPin.isNumericId(item)) {
                await this.getProductById(item);
            } else {
                await this.getProductByHandle(item);
            }
        }
    }
}

// 创建实例并启动置顶逻辑。
function startCollectionSortByPin(api) {
    const instance = new CollectionSortByPin(api);
    instance.start();
}

document.addEventListener('DOMContentLoaded', () => {
    if (window.ShopifyStorefrontAPI?.onInit) {
        window.ShopifyStorefrontAPI.onInit(() => {
            startCollectionSortByPin(window.ShopifyStorefrontAPI);
        });
    } else {
        document.addEventListener('ShopifyStorefrontAPI:init', (event) => {
            startCollectionSortByPin(event?.detail?.api || window.ShopifyStorefrontAPI);
        });
    }
});
